import React, { useState, useEffect, useCallback } from 'react';
import Header from './components/Header';
import SearchBar from './components/SearchBar';
import RecipeCard from './components/RecipeCard';
import RecipeModal from './components/RecipeModal';
import SkeletonCard from './components/SkeletonCard';
import { searchMeals, getRandomMeals } from './api';
import './App.css';

const VIEWS = { HOME: 'home', RESULTS: 'results' };

export default function App() {
  const [view, setView] = useState(VIEWS.HOME);
  const [meals, setMeals] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [loading, setLoading] = useState(false);
  const [featuredLoading, setFeaturedLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMeal, setSelectedMeal] = useState(null);
  const [lastQuery, setLastQuery] = useState('');

  // Load featured/random meals on mount
  useEffect(() => {
    setFeaturedLoading(true);
    getRandomMeals(8)
      .then(setFeatured)
      .catch(() => setFeatured([]))
      .finally(() => setFeaturedLoading(false));
  }, []);

  const handleSearch = useCallback(async (query) => {
    if (!query.trim()) return;
    setLoading(true);
    setError(null);
    setLastQuery(query);
    setView(VIEWS.RESULTS);
    try {
      const results = await searchMeals(query);
      setMeals(results);
      if (results.length === 0) {
        setError(`No recipes found for "${query}". Try a different search!`);
      }
    } catch (err) {
      setError('Something went wrong. Please check your connection and try again.');
      setMeals([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleHome = useCallback(() => {
    setView(VIEWS.HOME);
    setMeals([]);
    setError(null);
    setLastQuery('');
  }, []);

  return (
    <div className="app">
      <Header onLogoClick={handleHome} />

      <main className="main">
        {/* Hero / Search */}
        <section className={`hero ${view === VIEWS.RESULTS ? 'hero-compact' : ''}`}>
          {view === VIEWS.HOME && (
            <div className="hero-text">
              <h1 className="hero-heading">
                Find Your Next<br />
                <span className="hero-accent">Favourite Recipe</span>
              </h1>
              <p className="hero-sub">
                Search thousands of recipes from cuisines around the globe
              </p>
            </div>
          )}
          {view === VIEWS.RESULTS && (
            <div className="results-header">
              <button className="back-btn" onClick={handleHome}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
                Back
              </button>
              {!loading && !error && (
                <p className="results-count">
                  <strong>{meals.length}</strong> recipe{meals.length !== 1 ? 's' : ''} for
                  <em> "{lastQuery}"</em>
                </p>
              )}
            </div>
          )}
          <SearchBar onSearch={handleSearch} loading={loading} />
        </section>

        {/* Results / Featured */}
        <section className="content">
          {/* Error state */}
          {error && (
            <div className="error-state">
              <span className="error-emoji">🔍</span>
              <p>{error}</p>
              <button className="btn-ghost" onClick={handleHome}>Try again</button>
            </div>
          )}

          {/* Loading skeletons */}
          {loading && (
            <div className="grid">
              {Array.from({ length: 8 }).map((_, i) => (
                <SkeletonCard key={i} />
              ))}
            </div>
          )}

          {/* Search results */}
          {!loading && !error && view === VIEWS.RESULTS && meals.length > 0 && (
            <div className="grid">
              {meals.map((meal, i) => (
                <RecipeCard
                  key={meal.idMeal}
                  meal={meal}
                  onClick={setSelectedMeal}
                  index={i}
                />
              ))}
            </div>
          )}

          {/* Home: featured section */}
          {view === VIEWS.HOME && (
            <>
              <div className="section-label">
                <span className="section-label-line" />
                <span className="section-label-text">✨ Featured Today</span>
                <span className="section-label-line" />
              </div>
              {featuredLoading ? (
                <div className="grid">
                  {Array.from({ length: 8 }).map((_, i) => <SkeletonCard key={i} />)}
                </div>
              ) : (
                <div className="grid">
                  {featured.map((meal, i) => (
                    <RecipeCard
                      key={meal.idMeal}
                      meal={meal}
                      onClick={setSelectedMeal}
                      index={i}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </section>
      </main>

      {/* Footer */}
      <footer className="footer">
        <p>Powered by <a href="https://github.com/maharajabsc2003-del" target="_blank">Maharaja</a> · Built with React</p>
      </footer>

      {/* Recipe detail modal */}
      {selectedMeal && (
        <RecipeModal meal={selectedMeal} onClose={() => setSelectedMeal(null)} />
      )}
    </div>
  );
}
